import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import TwilioConfigGuide from '../../../components/ui/TwilioConfigGuide';
import otpService from '../../../utils/otpService';
import ServiceErrorAlert from '../../../components/ui/ServiceErrorAlert';

const PersonalInfoStep = ({ data, onUpdate, onNext, onBack, errors: parentErrors, setErrors: setParentErrors, isLoading }) => {
  const [otpLoading, setOtpLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [showTwilioGuide, setShowTwilioGuide] = useState(false);
  const [canResendOtp, setCanResendOtp] = useState(true);
  const [resendTimer, setResendTimer] = useState(60);
  const [otpSuccess, setOtpSuccess] = useState('');
  const [showConfigGuide, setShowConfigGuide] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [twilioError, setTwilioError] = useState(null);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [displayOTP, setDisplayOTP] = useState('');
  const [formData, setFormData] = useState(() => {
    return data || {};
  });
  const otpEnabled = false;

  // Sync formData with parent data whenever parent data changes
  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const updateFormData = (updates) => {
    const newFormData = { ...formData, ...updates };
    setFormData(newFormData);
    
    // Immediately update parent component
    if (onUpdate) {
      onUpdate(newFormData);
    }
  };

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  // Timer effect for OTP expiry
  useEffect(() => {
    let interval;
    if (otpEnabled && otpExpiry && otpSent && !otpVerified) {
      interval = setInterval(() => {
        const now = new Date()?.getTime();
        const expiryTime = new Date(otpExpiry)?.getTime();
        const timeLeft = Math.max(0, Math.floor((expiryTime - now) / 1000));

        setRemainingTime(timeLeft);

        if (timeLeft === 0) {
          setOtpSent(false);
          setOtpExpiry(null);
          setDisplayOTP('');
          setOtpError('OTP has expired. Please request a new one.');
          clearInterval(interval);
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpExpiry, otpSent, otpVerified]);

  // Enhanced phone verification check that works for both authenticated and guest users
  useEffect(() => {
    if (!otpEnabled) {
      return;
    }

    const checkPhoneVerification = async () => {
      if (formData?.mobileNumber?.length === 10) {
        const result = await otpService?.isPhoneVerified();
        if (result?.success && result?.data?.phoneVerified) {
          setOtpVerified(true);
          setOtpSent(true);
          setOtpSuccess(
            result?.data?.isGuest ?
            'Phone number verified successfully' :
            'Phone number already verified'
          );
        }
      }
    };

    checkPhoneVerification();
  }, [formData?.mobileNumber, otpEnabled]);

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }

    // Reset OTP state if mobile number changes
    if (field === 'mobileNumber' && value !== formData?.mobileNumber) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpError('');
      setOtpSuccess('');
      setOtpExpiry(null);
      setRemainingTime(0);
      setDisplayOTP('');
    }
  };

  const scrollToErrorField = (fieldName) => {
    const selectors = [
      `#input-${fieldName}`,
      `[name="${fieldName}"]`,
      `input[placeholder*="${fieldName}"]`,
      `.error-field-${fieldName}`];

    let fieldElement = null;

    for (const selector of selectors) {
      fieldElement = document.querySelector(selector);
      if (fieldElement) break;
    }

    if (!fieldElement) {
      const labels = document.querySelectorAll('label');
      for (const label of labels) {
        if (label?.textContent?.toLowerCase()?.includes(fieldName?.toLowerCase()?.replace(/([A-Z])/g, ' $1')?.trim())) {
          const inputId = label?.getAttribute('for');
          if (inputId) {
            fieldElement = document.getElementById(inputId);
            break;
          }
          fieldElement = label?.nextElementSibling?.querySelector('input') ||
            label?.nextElementSibling;
          if (fieldElement) break;
        }
      }
    }

    if (fieldElement) {
      const yOffset = -100;
      const elementTop = fieldElement?.getBoundingClientRect()?.top;
      const yPosition = elementTop + window.pageYOffset + yOffset;

      window.scrollTo({
        top: yPosition,
        behavior: 'smooth'
      });

      setTimeout(() => {
        fieldElement?.focus();
      }, 500);
    }
  };

  const handleSendOTP = async () => {
    if (!formData?.mobileNumber) {
      setOtpError('Please enter your mobile number first');
      return;
    }

    setIsSendingOTP(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      const result = await otpService?.sendOTP(formData?.mobileNumber);
      
      if (result?.success) {
        setOtpSent(true);
        setOtpSuccess(result?.data?.message || 'OTP sent successfully to your WhatsApp number');
        setCanResendOtp(false);
        setResendTimer(60);
        
        // Start countdown
        let interval = setInterval(() => {
          setResendTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setCanResendOtp(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        console.error('OTP send failed:', result);
        setOtpError(result?.error || 'Failed to send OTP');
        
        // Show configuration guide for setup issues
        if (result?.showConfigGuide || result?.requiresConfiguration) {
          setShowTwilioGuide(true);
          setTwilioError(result);
        }
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setOtpError('Network error. Please check your connection and try again.');
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formData?.otp || formData?.otp?.length !== 6) {
      setOtpError({
        error: 'Please enter a valid 6-digit OTP code',
        code: 'INVALID_FORMAT',
        category: 'validation'
      });
      return;
    }

    setIsVerifying(true);
    setOtpError(null);

    try {
      const result = await otpService?.verifyOTP(formData?.mobileNumber, formData?.otp);

      if (result?.success) {
        setOtpVerified(true);
        setIsPhoneVerified(true);
        setOtpSent(false);
        setOtpError(null);
        setOtpSuccess('Phone number verified successfully!');

        // Clear the form OTP field
        updateFormData({ otp: '' });
      } else {
        console.error('OTP verification failed:', result);
        setOtpError({
          ...result,
          retryable: false,
          category: result?.category || 'verification'
        });
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError({
        error: 'Failed to verify OTP. Please try again.',
        code: 'VERIFY_FAILED',
        technicalDetails: error?.message,
        retryable: false,
        category: 'network'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRetryOTP = () => {
    setOtpError(null);
    setOtpSuccess('');
    handleSendOTP();
  };

  const handleDismissError = () => {
    setOtpError(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const validateStep = () => {
    const newErrors = {};

    // Basic field validations
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData?.gender) newErrors.gender = 'Gender selection is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData?.mobileNumber?.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/?.test(formData?.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    // Fixed OTP verification validation logic
    if (!formData?.mobileNumber || formData?.mobileNumber?.length !== 10) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number before proceeding';
    } else if (otpEnabled) {
      // Only require OTP verification if phone is not already verified
      if (!isPhoneVerified && !otpVerified) {
        if (!otpSent) {
          newErrors.otp = 'Please send OTP to verify your mobile number';
        } else if (otpSent && !otpVerified) {
          newErrors.otp = 'Please verify your mobile number with the OTP sent';
        }
      }
    }

    // if (!formData?.aadharNumber?.trim()) {
    //   newErrors.aadharNumber = 'Aadhar number is required';
    // } else if (!/^\d{12}$/?.test(formData?.aadharNumber?.replace(/\s/g, ''))) {
    //   newErrors.aadharNumber = 'Please enter a valid 12-digit Aadhar number';
    // }
    if (!formData?.fatherName?.trim()) newErrors.fatherName = 'Father\'s name is required';
    if (!formData?.motherName?.trim()) newErrors.motherName = 'Mother\'s name is required';
    if (!formData?.address?.trim()) newErrors.address = 'Address is required';
    if (!formData?.city?.trim()) newErrors.city = 'City is required';
    if (!formData?.state?.trim()) newErrors.state = 'State is required';
    if (!formData?.pincode?.trim()) {
      newErrors.pincode = 'PIN code is required';
    } else if (!/^\d{6}$/?.test(formData?.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit PIN code';
    }

    setErrors(newErrors);

    // Enhanced error handling with priority scrolling for OTP
    if (Object.keys(newErrors)?.length > 0) {
      if (newErrors?.otp) {
        setTimeout(() => {
          if (!otpSent) {
            const mobileField = document.getElementById('mobileNumber');
            if (mobileField) {
              const yOffset = -150;
              const elementTop = mobileField?.getBoundingClientRect()?.top;
              const yPosition = elementTop + window.pageYOffset + yOffset;
              window.scrollTo({ top: yPosition, behavior: 'smooth' });

              const sendOtpButton = document.querySelector('button[data-send-otp]');
              if (sendOtpButton && formData?.mobileNumber?.length === 10) {
                sendOtpButton?.classList?.add('ring-2', 'ring-destructive', 'ring-offset-2');
                setTimeout(() => {
                  sendOtpButton?.classList?.remove('ring-2', 'ring-destructive', 'ring-offset-2');
                }, 3000);
              }
            }
          } else {
            scrollToErrorField('otp');
          }
        }, 100);
      } else {
        const firstErrorField = Object.keys(newErrors)?.[0];
        setTimeout(() => {
          scrollToErrorField(firstErrorField);
        }, 100);
      }
      return false;
    }

    return true;
  };

  const handleNext = () => {
    // Ensure parent has latest form data before validation
    if (onUpdate) {
      onUpdate(formData);
    }
    
    // Clear any existing parent errors
    if (setParentErrors) {
      setParentErrors({});
    }
    
    // Validate and proceed
    if (validateStep()) {
      // Final sync before moving to next step
      if (onUpdate) {
        onUpdate(formData);
      }
      
      // Call the parent's onNext function
      if (onNext) {
        onNext();
      }
    }
  };

  const handleNextDebug = () => {
    console.log('Next Step clicked');
    console.log('Form Data:', formData);
    console.log('OTP Verified:', otpVerified);
    console.log('Phone Verified:', isPhoneVerified);
    console.log('OTP Sent:', otpSent);
    
    handleNext();
  };

  const formatAadhar = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    const formatted = cleaned?.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
    return formatted;
  };

  return (
    <div className="space-y-6">
      {/* TwilioConfigGuide Modal */}
      {showTwilioGuide && (
        <TwilioConfigGuide
          error={twilioError}
          onClose={() => {
            setShowTwilioGuide(false);
            setTwilioError(null);
          }}
        />
      )}
      {/* Personal Details Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Icon name="User" size={18} color="var(--color-primary)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Personal Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            id="fullName"
            label="Full Name"
            type="text"
            placeholder="Enter your full name"
            value={formData?.fullName || ''}
            onChange={(e) => handleInputChange('fullName', e?.target?.value)}
            error={errors?.fullName}
            className={errors?.fullName ? 'error-field-fullName' : ''}
            required
          />

          <Input
            id="dateOfBirth"
            label="Date of Birth"
            type="date"
            value={formData?.dateOfBirth || ''}
            onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
            error={errors?.dateOfBirth}
            className={errors?.dateOfBirth ? 'error-field-dateOfBirth' : ''}
            required
          />

          <Select
            label="Gender"
            placeholder="Select your gender"
            options={genderOptions}
            value={formData?.gender || ''}
            onChange={(value) => handleInputChange('gender', value)}
            error={errors?.gender}
            required
          />

          <Input
            id="email"
            label="Email Address"
            type="email"
            placeholder="Enter your email address"
            value={formData?.email || ''}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            className={errors?.email ? 'error-field-email' : ''}
            required
          />
        </div>
      </div>
      {/* Contact Details Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-secondary/10 rounded-lg">
            <Icon name="Phone" size={18} color="var(--color-secondary)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              id="mobileNumber"
              label="Whatsapp Number"
              type="tel"
              placeholder="Enter 10-digit mobile number"
              value={formData?.mobileNumber || ''}
              onChange={(e) => handleInputChange('mobileNumber', e?.target?.value?.replace(/\D/g, '')?.slice(0, 10))}
              error={errors?.mobileNumber}
              className={errors?.mobileNumber ? 'error-field-mobileNumber' : ''}
              required />

            {/* Enhanced OTP Error Display using ServiceErrorAlert */}
            {otpEnabled && otpError &&
              <div className="mt-4">
                <ServiceErrorAlert
                  error={otpError}
                  onRetry={otpSent ? handleRetryOTP : handleSendOTP}
                  onDismiss={handleDismissError}
                  showRetry={otpError?.retryable !== false}
                  showDismiss={!otpError?.persistent}
                  className="mb-4" />
              </div>
            }

            {/* Success Message */}
            {otpEnabled && otpSuccess && !otpError &&
              <div className="flex items-start space-x-2 p-4 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" strokeWidth={2} />
                <div className="flex-1">
                  <span className="text-sm font-medium text-success">{otpSuccess}</span>
                  {remainingTime > 0 && otpSent &&
                    <p className="text-xs text-success/80 mt-1">
                      Code expires in: {formatTime(remainingTime)}
                    </p>
                  }
                </div>
              </div>
            }

            {/* Send OTP Button */}
            {otpEnabled && formData?.mobileNumber && formData?.mobileNumber?.length === 10 && !otpSent && !otpVerified &&
              <div className="space-y-2">
                <Button
                  variant="outline"
                  onClick={handleSendOTP}
                  loading={isSendingOTP}
                  iconName="Send"
                  iconPosition="right"
                  className="w-full"
                  data-send-otp="true"
                  disabled={isSendingOTP}>

                  {isSendingOTP ? 'Sending OTP...' : 'Send OTP to WhatsApp'}
                </Button>
              </div>
            }

            {/* OTP Verification Input */}
            {otpEnabled && otpSent && !otpVerified &&
              <div className="space-y-3">
                <Input
                  label="Enter OTP"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={formData?.otp || ''}
                  onChange={(e) => handleInputChange('otp', e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                  error={errors?.otp}
                  description={remainingTime > 0 ? `OTP expires in: ${formatTime(remainingTime)}` : 'Enter the OTP code sent to your WhatsApp'}
                  className="text-center text-lg tracking-wider"
                  required />

                <div className="flex space-x-2">
                  <Button
                    variant="default"
                    onClick={handleVerifyOTP}
                    loading={isVerifying}
                    iconName="Check"
                    iconPosition="right"
                    className="flex-1"
                    disabled={isVerifying || !formData?.otp || formData?.otp?.length !== 6}>

                    {isVerifying ? 'Verifying...' : 'Verify OTP'}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={handleRetryOTP}
                    loading={isSendingOTP}
                    iconName="RefreshCw"
                    iconPosition="right"
                    disabled={isSendingOTP || remainingTime > 240} // Allow resend in last minute
                    title={remainingTime > 240 ? `Wait ${formatTime(remainingTime - 240)} to resend` : 'Send new OTP'}>

                    Resend
                  </Button>
                </div>
              </div>
            }

            {/* Verified Status */}
            {/* {(otpVerified || isPhoneVerified) &&
              <div className="flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" strokeWidth={2} />
                <span className="text-sm font-medium text-success">Mobile number verified successfully</span>
              </div>
            } */}
          </div>

          {/* Enhanced OTP Verification Section */}
          <div className="bg-surface border border-border rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3 flex items-center">
              <Icon name="Shield" size={18} className="mr-2" />
              Phone Verification Status
            </h3>

            {/* Current Status Display */}
            {!otpEnabled &&
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Info" size={16} />
                  <span className="text-sm">Verification temporarily disabled</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  WhatsApp OTP verification is currently unavailable, but you can continue your registration.
                </p>
              </div>
            }

            {otpEnabled && !isPhoneVerified && !otpVerified && !otpSent &&
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span className="text-sm">Verification pending</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Enter your mobile number and click "Send OTP" to verify via WhatsApp
                </p>
              </div>
            }

            {otpEnabled && otpSent && !otpVerified &&
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-warning">
                  <Icon name="MessageSquare" size={16} />
                  <span className="text-sm font-medium">OTP sent to WhatsApp</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Check your WhatsApp and enter the 6-digit code above
                </p>
                {remainingTime > 0 &&
                  <p className="text-xs text-muted-foreground">
                    Time remaining: {formatTime(remainingTime)}
                  </p>
                }
              </div>
            }

            {otpEnabled && (isPhoneVerified || otpVerified) &&
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-success">
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm font-medium">Phone number verified</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your mobile number has been successfully verified
                </p>
              </div>
            }

            {/* Quick Actions */}
            {otpEnabled && otpError?.requiresConfiguration &&
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Need help with SMS setup?</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfigGuide(true)}
                  iconName="ExternalLink"
                  iconPosition="right"
                  className="text-xs">
                  View Configuration Guide
                </Button>
              </div>
            }
          </div>
        </div>
      </div>
      {/* Government ID Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-accent/10 rounded-lg">
            <Icon name="CreditCard" size={18} color="var(--color-accent)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Government ID</h3>
        </div>

        <Input
          id="aadharNumber"
          label="Aadhar Card Number"
          type="text"
          placeholder="Enter 12-digit Aadhar number"
          value={formData?.aadharNumber || ''}
          onChange={(e) => handleInputChange('aadharNumber', formatAadhar(e?.target?.value)?.slice(0, 14))}
          error={errors?.aadharNumber}
          className={errors?.aadharNumber ? 'error-field-aadharNumber' : ''}
          description="Your Aadhar information is secure and encrypted"
          />

      </div>
      {/* Family Details Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-lg">
            <Icon name="Users" size={18} color="var(--color-success)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Family Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            id="fatherName"
            label="Father's Name"
            type="text"
            placeholder="Enter father's full name"
            value={formData?.fatherName || ''}
            onChange={(e) => handleInputChange('fatherName', e?.target?.value)}
            error={errors?.fatherName}
            className={errors?.fatherName ? 'error-field-fatherName' : ''}
            required />


          <Input
            id="motherName"
            label="Mother's Name"
            type="text"
            placeholder="Enter mother's full name"
            value={formData?.motherName || ''}
            onChange={(e) => handleInputChange('motherName', e?.target?.value)}
            error={errors?.motherName}
            className={errors?.motherName ? 'error-field-motherName' : ''}
            required />

        </div>
      </div>
      {/* Address Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-warning/10 rounded-lg">
            <Icon name="MapPin" size={18} color="var(--color-warning)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Residential Address</h3>
        </div>

        <div className="space-y-6">
          <Input
            id="address"
            label="Complete Address"
            type="text"
            placeholder="House/Flat No., Street, Area, Landmark"
            value={formData?.address || ''}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            error={errors?.address}
            className={errors?.address ? 'error-field-address' : ''}
            required />


          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              id="city"
              label="City"
              type="text"
              placeholder="Enter city"
              value={formData?.city || ''}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              error={errors?.city}
              className={errors?.city ? 'error-field-city' : ''}
              required />


            <Input
              id="state"
              label="State"
              type="text"
              placeholder="Enter state"
              value={formData?.state || ''}
              onChange={(e) => handleInputChange('state', e?.target?.value)}
              error={errors?.state}
              className={errors?.state ? 'error-field-state' : ''}
              required />


            <Input
              id="pincode"
              label="PIN Code"
              type="text"
              placeholder="Enter 6-digit PIN"
              value={formData?.pincode || ''}
              onChange={(e) => handleInputChange('pincode', e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
              error={errors?.pincode}
              className={errors?.pincode ? 'error-field-pincode' : ''}
              required />

          </div>
        </div>
      </div>
      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4 pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          iconName="ChevronLeft"
          iconPosition="left"
          className="sm:w-auto w-full">
          Previous
        </Button>

        <div className="flex space-x-4">
          <Button
            variant="ghost"
            iconName="Save"
            iconPosition="left"
            className="sm:w-auto w-full">
            Save Draft
          </Button>

          <Button
            variant="default"
            onClick={handleNextDebug}
            iconName="ChevronRight"
            iconPosition="right"
            className="sm:w-auto w-full"
            disabled={isLoading}>
            Next Step
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoStep;
